import { getTag, useNavigation, useTranslation } from '@msdk/common'
import {
  AllFormButtons,
  AutoFormButtons,
  ButtonAlign,
  CustomActionButtonAspect,
  CustomActionDefinition,
  EntityAcl,
  EntityContext,
  EntityModel,
  EntityOperation,
  FormActionButtonType,
  FormModel,
  FormNode,
  SaveButtonVisibility,
} from '@msdk/definitions'
import { Box, COLORS, Spinner, styled, THEME_CONST, Z_INDEX } from '@msdk/ui'
import React, { useState } from 'react'

import { useEntryFromCollectionWithInternalId } from '../../state'
import { CustomLogicInvoker } from '../DynamicForm/custom-logic-invoker'
import { useEnableFormActions } from '../FormHeader/filter-form-action.hook'
import { FormActions } from './FormActions'

const DefaultActionsContainer = styled(Box, {
  shouldForwardProp: (prop) => prop !== 'isModalActions',
})<{ isModalActions: boolean | undefined }>`
  display: flex;
  flex-direction: row;
  justify-content: flex-end;
  gap: 8px;
  ${(props) =>
    props.isModalActions &&
    `
    padding-bottom: 0;
  `}
  ${(props) =>
    !props.isModalActions &&
    `
    padding-right: 16px;
    padding-top: 16px;
    padding-left: ${THEME_CONST.sideMenuWidth + 16}px;
    padding-bottom: 16px;

    border-top: 1px solid ${COLORS.border};
    position: fixed;
    bottom: 0;
    left: 0;
    background-color: ${COLORS.white.main};
    // -1 to be under sidebar
    z-index: ${Z_INDEX.under_everything - 1};
  `}
`

const StyledSpinner = styled(Spinner)`
  color: white;
  width: 20px !important;
  height: 20px !important;
`

interface Props {
  onSave: (actionId?: string | undefined) => Promise<EntityOperation>
  onDelete: () => Promise<EntityOperation>
  onRestore: () => Promise<EntityOperation>
  onReloadEntity: () => Promise<void>
  onMoveToBin: () => Promise<EntityOperation>
  onClose: () => void
  rebindEntity: (entity: EntityContext, pendingChanges?: boolean) => void
  entity: EntityContext
  entityModel: EntityModel
  formModel: FormModel
  rootNode: FormNode
  acl: EntityAcl
  invoker: CustomLogicInvoker
  entityLoaded: boolean
  isModalActions?: boolean
  isFullScreen?: boolean
  selectedTab?: number
  isFirstTab?: boolean
  isLastTab?: boolean
  moveToTab?: (tabIndex: number) => void
}

export function ActionsContainer(props: Props) {
  const t = useTranslation()
  const frameworkNavigate = useNavigation()
  const entityEntry = useEntryFromCollectionWithInternalId(props.entity?.internalId as string)
  const [isSaving, setIsSaving] = useState(false)

  const tryGetSaveButtonLabel = () => {
    const { labels } = props.formModel
    if (props.entity.IsPersisted && labels?.saveButton) {
      return t(labels.saveButton.idml, labels.saveButton.description)
    }

    if (!props.entity.IsPersisted && labels?.createButton) {
      return t(labels.createButton.idml, labels.createButton.description)
    }
  }

  const save = async () => {
    if (!isSaving) {
      setIsSaving(true)
      await props.onSave()
      setIsSaving(false)
    }
  }

  const isValid = () => {
    if (entityEntry) {
      return entityEntry.pendingChanges || !props.entity.IsPersisted
    }
    return false
  }

  const maxWidthStyle = { width: `100%` }

  const enableActions = useEnableFormActions(
    props.entityModel.name,
    props.entity,
    props.formModel.name,
    props.formModel.customActions as CustomActionDefinition[],
    'Edit',
    props.acl,
    props.rootNode,
    frameworkNavigate,
    props.onSave,
    props.onReloadEntity,
    props.entityLoaded,
    props.rebindEntity,
    props.selectedTab,
    props.isFirstTab,
    props.isLastTab,
    props.moveToTab,
  )

  const isSaveButtonVisible = () => {
    const isCreate = !props.entity.IsPersisted
    if (isCreate) {
      return (
        props.formModel.saveButtonVisibility === SaveButtonVisibility.Visible ||
        props.formModel.saveButtonVisibility === SaveButtonVisibility.OnlyCreate
      )
    } else {
      return (
        props.formModel.saveButtonVisibility === SaveButtonVisibility.Visible ||
        props.formModel.saveButtonVisibility === SaveButtonVisibility.OnlySave
      )
    }
  }

  const isModalActions = props.isModalActions ?? false

  const autoFormButtons: AutoFormButtons[] = []
  autoFormButtons.push({
    id: 'Save',
    alignment: ButtonAlign.Right,
    onClick: () => save(),
    label: (
      <>
        {!isSaving && props.entity.IsPersisted && (tryGetSaveButtonLabel() ?? t('CMPSR_SAVE', 'Save'))}
        {!isSaving && !props.entity.IsPersisted && (tryGetSaveButtonLabel() ?? t('CMPSR_CREATE', 'Create'))}
        {isSaving && <StyledSpinner />}
      </>
    ),
    testId: getTag(props.formModel.name, '👻', 'save', 'button'),
    aspect: CustomActionButtonAspect.Primary,
    disabled: !isValid(),
    visibility: props.formModel.saveButtonVisibility,
  })

  if (!props.isModalActions || !props.isFullScreen) {
    autoFormButtons.push({
      id: 'Cancel',
      alignment: ButtonAlign.Right,
      onClick: props.onClose,
      label: t('CMPSR_CANCEL', 'Cancel'),
      testId: getTag(props.formModel.name, props.formModel.name, 'close', 'button'),
      aspect: CustomActionButtonAspect.Secondary,
    })
  }

  /*
   * Create an array with all the visible buttons of type 'button' on the form.
   * The 'type' is used to distinguish between automatically generated buttons (FormActionButtonType.Auto) and custom buttons (FormActionButtonType.Custom),
   * so you know which array the 'id' corresponds to in order to retrieve the associated information for that button.
   * The 'alignment' is used to sort the array by separating the buttons aligned to the left from those aligned to the right.
   */
  const allFormButtons: AllFormButtons[] = []

  /*
   * Add custom buttons to the array.
   */
  for (const button of enableActions.customActions) {
    allFormButtons.push({ id: button.name, type: FormActionButtonType.Custom, alignment: button.buttonAlign })
  }

  /*
   * Add 'Cancel' button to the array.
   */
  if (!props.isFullScreen) {
    allFormButtons.push({
      id: 'Cancel',
      type: FormActionButtonType.Auto,
      alignment: props.formModel.buttonCancelAlign,
    })
  }

  /*
   * Add 'Save' button to the array only if it is visible.
   */
  if (isSaveButtonVisible()) {
    allFormButtons.push({ id: 'Save', type: FormActionButtonType.Auto, alignment: props.formModel.buttonSaveAlign })
  }

  /*
   * Maps ButtonAlign enum values to numeric order values for sorting buttons.
   * Buttons aligned to the left (ButtonAlign.Left) get an order of 10,
   * while buttons aligned to the right (ButtonAlign.Right) get an order of 20.
   */
  const alignmentOrder: Record<ButtonAlign, number> = {
    [ButtonAlign.Left]: 10,
    [ButtonAlign.Right]: 20,
  }

  /*
   * Sort buttons array by their alignment property
   */
  allFormButtons.sort((a, b) => {
    return alignmentOrder[a.alignment] - alignmentOrder[b.alignment]
  })

  return (
    <DefaultActionsContainer isModalActions={isModalActions} width={maxWidthStyle}>
      {props.entity?.fields && (
        <FormActions
          entityName={props.entityModel.name}
          formName={props.formModel.name}
          entity={props.entity}
          formNode={props.rootNode}
          acl={props.acl}
          invoker={props.invoker}
          navigate={frameworkNavigate}
          save={props.onSave}
          data-test-id={getTag(props.formModel.name, props.entityModel.name, props.formModel.name, 'button')}
          moveToBin={props.onMoveToBin}
          restore={props.onRestore}
          delete={props.onDelete}
          reloadEntity={props.onReloadEntity}
          mode="Edit"
          enabledFormActions={enableActions}
          autoFormButtons={autoFormButtons}
          allFormButtons={allFormButtons}
          visualization="Buttons"
        />
      )}
    </DefaultActionsContainer>
  )
}
